// src/shared-components/forms/add-board-content/add-post/inside-header/index.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useGoBack from "@/hooks/useGoBack";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from '@/i18n/useTranslations';
import GalleryService from "@/services/gallery.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import PublicationDatePopup from "@/shared-components/forms/add-board-content/add-article/inside-header/components/PublicationDatePopup";
import SaveAsDraftDialog from "@/shared-components/forms/add-board-content/add-article/inside-header/components/SaveAsDraftDialog";
import AudienceSelectionPopup from '@/shared-components/forms/add-board-content/shared-components/AudienceSelectionPopup';
import CalendarIcon from "@/shared-components/icons/components/forms/CalendarIcon";
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from "@/shared-components/loader/Loader";
import Utils from '@/utils';
import { buildPublicContentPath } from '@/utils/contentRoutes';

import { getPayload } from '../../saveFunctions';

import SavePostDraft from "./components/SavePostDraft";

import "@/shared-components/forms/add-board-content/add-article/inside-header/index.css";

export default function Header({
  formState,
  setFormState,
  formError,
  setFormError,
  editMode,
  onDraftDialogToggle,
  editableRef,
  fetchData = {},
  membersAudienceEnabled = false
}) {
  const t = useTranslations('common.forms.board_editor');
  const { cacheHandler } = useStateHandler();
  const [openSaveAsDraft, setOpenSaveAsDraft] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openPublicationDate, setOpenPublicationDate] = useState(false);
  const [openAudienceSelection, setOpenAudienceSelection] = useState(false);
  const goBackHandler = useGoBack("/profile/board");
  const initialState = useRef(formState);

  // — as soon as we're editing a saved draft (incomplete + has an _id),
  //   reset our "baseline" so SavePostDraft is disabled until real changes occur.
  useEffect(() => {
    if (editMode && formState._id && formState.post_status === "incomplete") {
      initialState.current = formState;
    }
  }, [editMode, formState._id, formState.post_status, formState]);
  
  const navigate = useNavigate();

  // we get the structure to ensure that we have a body text
  const tempPayload = getPayload(editableRef, formState);

  const hasText     =  Boolean((tempPayload.post_texts || []).length > 0);
  const hasPictures = (formState.post_pictures?.length  || 0) > 0;
  const hasArt      = (formState.post_artworks?.length  || 0) > 0;
  const hasPolls    = (formState.post_polls?.length     || 0) > 0;
  const hasQuizzes  = (formState.post_quizzes?.length   || 0) > 0;
  const hasContent  = hasText || hasPictures || hasArt || hasPolls || hasQuizzes;
  const hasScheduledPublication = Boolean(formState.publicationDate && formState.publicationTime);
  const isCalendarActive = hasScheduledPublication;

  // As soon as we mount into an edit-draft (i.e. editMode + real draft _id),
  // reset our "initial snapshot" so SaveDraft sees no changes yet.
  useEffect(() => {
    if (editMode && formState._id && fetchData.post_status === "incomplete") {
      initialState.current = formState;
    }
  }, [editMode, formState._id, fetchData.post_status, formState]);

  // Whether we're editing an incomplete draft.
  const isDraftMode = editMode && fetchData.post_status === "incomplete";
  // same test: show inline button if new OR (editing AND incomplete)
  // 2) Only show the inline Save button when creating a new post (not editMode)
  //    OR when *editing* and it's still incomplete
  const showSave = !editMode || isDraftMode;

  useEffect(() => {
    onDraftDialogToggle?.(openSaveAsDraft);
  }, [openSaveAsDraft, onDraftDialogToggle]);

  async function savePost(onSaveCallback, customFields = {}) {
    setLoading(true);
    setFormError(null);

    if (Utils.isPostMediaUploading(formState)) { 
      setLoading(false);
      return;
    }

    // get payload
    let payload = getPayload(editableRef, formState);
    const hasSchedule = Boolean(formState.publicationDate && formState.publicationTime);

    if (hasSchedule) {
      payload.publishedAt              = formState.publishedAt;
      payload.publicationDate          = formState.publicationDate;
      payload.publicationTime          = formState.publicationTime;
      payload.publicationTimezone      = formState.publicationTimezone;
      payload.publicationOffsetMinutes = formState.publicationOffsetMinutes;
      payload.post_status              = 'queued';
    } else {
      payload.publishedAt              = null;
      payload.publicationDate          = '';
      payload.publicationTime          = '';
      payload.publicationTimezone      = null;
      payload.publicationOffsetMinutes = null;
      payload.post_status              = 'published';
    }

    payload = {
      ...payload,
      ...customFields,
    };

    const apiResponse = await GalleryService.submitPost(payload, true);
    setLoading(false);
    onSaveCallback(apiResponse);
  }

  const onPublishCallback = (apiResponse) => {
    const { response, data } = apiResponse;
  
    if (response.ok) {
      cacheHandler.triggerAction("SUBMIT_POST");
      cacheHandler.storeInCache(`post_${data._id}_view`, data);
      navigate(buildPublicContentPath(data, 'post'), { replace: true, state: { from: true } });
      if (editMode) navigate(-1);
    } else if (response.status === 400) {
      setFormError({ element: data.error_data.element, error_code: data.error_type });
    }
  };

  const onDraftCallback = (apiResponse) => {
    const { response, data } = apiResponse;
    if (response.ok) {
      cacheHandler.triggerAction("POST_DRAFT_SAVED");
      setFormState(prev => ({
        ...prev,
        _id: data._id,
      }));
    } else {
      alert('Could not save draft.');
    }
  };

  const handlePublish = () => {
    if (membersAudienceEnabled) {
      setOpenAudienceSelection(true);
      return;
    }

    savePost(onPublishCallback, { audience: 'public' });
  };

  const handleAudienceSelect = audience => {
    savePost(onPublishCallback, { audience });
  };

  return (
    <>
      <div className="board-header">
        <div className="board-header__left-options">
          <XIcon
            className="board-header__back-button"
            onClick={() => {
              if (JSON.stringify(initialState.current) !== JSON.stringify(formState) && !editMode) {
                setOpenSaveAsDraft(true);
              } else {
                goBackHandler();
              }
            }}
          />

          {/* only render on new OR incomplete‐draft */}
          {showSave && (
            <SavePostDraft
              formState={formState}
              initialStateRef={initialState}
              savePost={() => savePost(onDraftCallback, { post_status: 'incomplete' })}
            />
          )}
        </div>

        <h1 className="board-header__heading">
          {editMode ? t('edit_post') : t('post')}
        </h1>

        <div className="board-header__right-options">
          <button
            type="button"
            className={`board-header__calendar-button${isCalendarActive ? ' board-header__calendar-button--active' : ''}`}
            onClick={() => setOpenPublicationDate(true)}
            aria-label={t('schedule_publication')}
            aria-pressed={isCalendarActive}
          >
            <CalendarIcon className="board-header__calendar-icon" />
          </button>
          {/* no preview for posts */}
          <ContinueButton
            turnOff={!hasContent}
            loading={loading}
            className="board-header__item publish element-non-selectable board-header__continue-small"
            onClick={handlePublish}
          >
            {t('publish')}
            {loading && <Loader className="board-header__spinner" thin />}
          </ContinueButton>
        </div>
      </div>

      {!editMode && (
        <SaveAsDraftDialog
          formState={formState}
          open={openSaveAsDraft}
          close={() => setOpenSaveAsDraft(false)}
          mode="post"
        />
      )}

      <PublicationDatePopup
        open={openPublicationDate}
        onClose={() => setOpenPublicationDate(false)}
        formState={formState}
        setFormState={setFormState}
        formError={formError}
        setFormError={setFormError}
      />

      <AudienceSelectionPopup
        open={openAudienceSelection}
        onClose={() => setOpenAudienceSelection(false)}
        onSelect={handleAudienceSelect}
      />
    </>
  );
}
